<template>
    <b-container id="project-assignment-console" fluid>
        <b-row>
            <b-col cols="12">
                <div class="border-bottom mb-3 pb-3 d-flex align-items-end justify-content-between flex-wrap">
                    <div>
                        <h3 class="mb-1">프로젝트 배정 콘솔</h3>
                        <p class="fw-400 text-secondary mb-0">
                            프로젝트를 고르면 신청 질문과 배정 규칙을 한 화면에서 볼 수 있습니다.
                        </p>
                    </div>
                    <div class="d-flex align-items-center gap-2 mt-2">
                        <!-- 저장·삭제 뒤 바뀐 영역만 다시 받는 중 (화면은 그대로) -->
                        <span v-if="refreshing > 0 && !loading" class="text-secondary small d-flex align-items-center">
                            <b-spinner small class="mr-1"/>갱신 중
                        </span>
                        <b-button
                            size="sm"
                            variant="outline-primary"
                            :disabled="!selectedProjectId || loading || !!loadError"
                            @click="openPreview(null)"
                        >
                            <i class="bi bi-play-circle mr-1"></i>배정 미리보기
                        </b-button>
                        <!-- 고급 화면 이름을 함께 적는다(그 화면에서는 같은 대상을 다른 이름으로 부른다) -->
                        <b-dropdown size="sm" variant="outline-secondary" right text="고급">
                            <b-dropdown-item @click="goAdvanced('ManagingProjectSchema', 'schemaInput')">
                                신청 양식 · 선택지 (고급 · 스키마 입력 값)
                            </b-dropdown-item>
                            <b-dropdown-item @click="goAdvanced('ManagingProjectSchema', 'localeString')">
                                언어별 문구 (고급 · 다국어 문자열)
                            </b-dropdown-item>
                            <b-dropdown-divider/>
                            <b-dropdown-item @click="goAdvanced('ManagingCampaignConnectRule', 'condition')">
                                답변 조건 (고급 · 조건 관리)
                            </b-dropdown-item>
                            <b-dropdown-item @click="goAdvanced('ManagingCampaignConnectRule', 'ruleGroup')">
                                조건 묶음 (고급 · 규칙 그룹 관리)
                            </b-dropdown-item>
                            <b-dropdown-item @click="goAdvanced('ManagingCampaignConnectRule', 'campaignRule')">
                                배정 규칙 (고급 · 캠페인 규칙 관리)
                            </b-dropdown-item>
                        </b-dropdown>
                    </div>
                </div>
            </b-col>
        </b-row>

        <!-- 프로젝트 선택 -->
        <b-row class="mb-3">
            <b-col cols="12" md="8" lg="6">
                <label for="console-project-select" class="fw-500 mb-1">프로젝트</label>
                <!-- 사용자가 고르거나 지웠을 때만 URL 에 남긴다(@input). URL 에서 복원할 때는 기록을 만들지 않는다 -->
                <auto-complete-select
                    id="console-project-select"
                    ref="projectSelect"
                    v-model="selectedProjectId"
                    :options="projectCodeOptions"
                    :loading="projectsLoading"
                    start-expanded
                    clearable
                    placeholder="프로젝트를 선택해주세요."
                    @input="onProjectPicked"
                />
            </b-col>
        </b-row>

        <template v-if="selectedProjectId">
            <div v-if="loading" class="text-center py-5">
                <b-spinner/>
                <div class="text-secondary mt-2">불러오는 중…</div>
            </div>

            <b-alert v-else-if="loadError" variant="danger" show>
                <i class="bi bi-exclamation-triangle-fill mr-1"></i>{{ loadError }}
                <b-button size="sm" variant="outline-danger" class="ml-2" @click="reload">다시 시도</b-button>
            </b-alert>

            <template v-else>
                <!-- 준비 상태 배너 -->
                <b-row class="mb-3">
                    <b-col cols="12">
                        <readiness-banner :readiness="readiness" @cta="onBannerCta"/>
                    </b-col>
                </b-row>

                <!-- ① 신청 질문 -->
                <b-row>
                    <b-col cols="12">
                        <question-section
                            :input-keys="inputKeys"
                            :missing-keys="missingKeys"
                            @add="openQuestionModal('add')"
                            @edit="openQuestionModal('edit', $event)"
                            @reorder="openOrderModal"
                            @create-missing="openMissingQuestion"
                        />
                    </b-col>
                </b-row>

                <!-- ② 배정 규칙 -->
                <b-row>
                    <b-col cols="12">
                        <assignment-rule-section
                            :rule-sets="ruleSets"
                            :total="ruleSetTotal"
                            :truncated="ruleSetTruncated"
                            :key-map="keyMap"
                            :missing-keys="missingKeys"
                            :fallback-campaign-name="fallbackCampaignName"
                            @add="openRuleBuilder"
                            @edit="openRuleModal('edit', $event)"
                            @detail="openRuleModal('detail', $event)"
                            @preview="openPreview"
                        />
                    </b-col>
                </b-row>
            </template>
        </template>

        <!-- 목록을 못 받았으면 '프로젝트를 선택하라' 대신 오류와 다시 시도를 보여 준다 -->
        <p-page-empty-component
            v-else
            text="먼저 프로젝트를 선택해주세요."
            :loading="projectsLoading"
            loading-text="프로젝트 목록을 불러오는 중입니다."
            :error="projectsError"
            @retry="retryProjects"
        />

        <assignment-preview-modal
            :modal-id="previewModalId"
            :project-id="selectedProjectId"
            :input-keys="inputKeys"
            :campaigns="campaigns"
            :seed-rule-set="previewSeed"
        />

        <!-- 인플레이스 편집: 기존 관리 모달을 그대로 임베드(신규 저장 로직 없음).
             저장/삭제/순서변경 후에는 바뀐 영역만 다시 불러 배너·질문·규칙을 갱신한다
             (입력키 삭제 시 missing 배너 = 안전그물). -->
        <schema-input-modal
            ref="questionModal"
            :detail-id="modalQuestionId"
            :selected-project-code-id="selectedProjectId"
            :projects="projects"
            allow-copy-import
            lock-project-to-selected
            @request-copy-import="openCopyPicker"
            @addData="onQuestionsChanged"
            @updateDetailData="onQuestionsChanged"
            @deleteData="onQuestionsChanged"
        />
        <!-- 다른 스키마에서 복사: 전체 입력 값 목록에서 하나를 골라 질문 모달에 draft 로 시드.
             저장은 유저가 직접(openAsCopy 는 폼만 채운다). -->
        <schema-input-copy-picker-modal
            :projects="projects"
            @select="onCopyImportSelect"
        />
        <schema-input-order-change-modal
            :project-text="selectedProjectText"
            :schema-inputs="inputKeys"
            @schemaInputsOrderChanged="onQuestionsChanged"
        />
        <campaign-rule-set-modal
            ref="ruleModal"
            :detail-id="modalRuleId"
            :campaigns="campaigns"
            :projects="projects"
            @addData="onRulesChanged"
            @updateDetailData="onRulesChanged"
            @deleteData="onRulesChanged"
        />
        <!-- 규칙 추가는 문장 빌더로: 질문 드롭다운을 등록 입력키만으로 채워 미등록 key 참조를 원천 차단.
             저장은 조건→규칙그룹→규칙셋(마지막) 안전순서. -->
        <assignment-rule-sentence-builder
            ref="ruleBuilder"
            :modal-id="builderModalId"
            :project-id="selectedProjectId"
            :project-code="selectedProjectCode"
            :input-keys="inputKeys"
            :campaigns="campaigns"
            @created="onRulesChanged"
            @create-question="openQuestionModal('add')"
        />
    </b-container>
</template>

<script>
import {mapState} from 'vuex';
import {getErrorMessage} from '@core/utils/api/errorMessage';
import AutoCompleteSelect from '@core/components/common/AutoCompleteSelect.vue';
import PageEmptyComponent from '@shared/components/admin/components/common/PageEmptyComponent.vue';
import {getCampaignReadiness, getProjectSchema} from '@shared_utils/api/projectSchema';
import {listCampaignRuleSets, getCampaignRuleSet} from '@shared_utils/api/ruleEngine';
import {getProjectCodeOptions, getTargetCampaignText, getTargetProjectText} from '@shared/components/admin/pages/Management/common-data';
import {buildKeyMap} from './plain-language';
import ReadinessBanner from './ReadinessBanner.vue';
import QuestionSection from './QuestionSection.vue';
import AssignmentRuleSection from './AssignmentRuleSection.vue';
import AssignmentPreviewModal from './AssignmentPreviewModal.vue';
import AssignmentRuleSentenceBuilder from './AssignmentRuleSentenceBuilder.vue';
import SchemaInputModal from '@shared/components/admin/pages/Management/ProjectSchema/SchemaInput/SchemaInputModal.vue';
import SchemaInputOrderChangeModal from '@shared/components/admin/pages/Management/ProjectSchema/SchemaInput/SchemaInputOrderChangeModal.vue';
import SchemaInputCopyPickerModal from './SchemaInputCopyPickerModal.vue';
import CampaignRuleSetModal from '@shared/components/admin/pages/Management/CampaignConnectRule/CampaignRuleSet/CampaignRuleSetModal.vue';

const ROUTE_NAME = 'ManagingProjectConsole';
// 실패 문구에 어느 단계가 실패했는지 붙인다
const PART_LABELS = {readiness: '준비 상태', schema: '신청 질문', rules: '배정 규칙'};
// 프로젝트 필터가 있는 고급 화면 탭 — 보던 프로젝트를 넘긴다
const PROJECT_FILTER_TABS = ['schemaInput', 'campaignRule'];
// 고급 화면의 첫 탭(ProjectSchema·CampaignConnectRule) — 기본값이라 URL 에서 tab 을 뺀다
const DEFAULT_TABS = ['schemaInput', 'condition'];
// 프로젝트가 바뀌면 함께 닫을 콘솔 모달
const CONSOLE_MODAL_IDS = ['assignment-preview-modal', 'schema-input-copy-picker-modal', 'schema-input-order-change-modal'];

// URL 의 project_id 는 양의 정수 문자열만 받는다('01'·'1.5'·'1e3'·배열은 없는 값)
const parseProjectId = (value) => {
    if (typeof value !== 'string' || !/^[1-9]\d*$/.test(value)) return null;
    const id = Number(value);
    return Number.isSafeInteger(id) ? id : null;
};

export default {
    name: 'ProjectConsole',
    components: {
        AutoCompleteSelect,
        'p-page-empty-component': PageEmptyComponent,
        ReadinessBanner,
        QuestionSection,
        AssignmentRuleSection,
        AssignmentPreviewModal,
        AssignmentRuleSentenceBuilder,
        SchemaInputModal,
        SchemaInputOrderChangeModal,
        SchemaInputCopyPickerModal,
        CampaignRuleSetModal,
    },
    data() {
        return {
            projects: null,
            projectsLoading: false,
            projectsError: '',
            campaigns: [],
            selectedProjectId: null,
            loading: false,
            loadError: null,
            // 화면을 유지한 채 다시 받는 중인 요청 수
            refreshing: 0,
            readiness: null,
            inputKeys: [],
            ruleSetsRaw: [],
            ruleSetTotal: 0,
            ruleSetTruncated: false,
            // 규칙 카드 '미리보기' 로 열면 그 규칙의 조건 값을 미리 채운다 (헤더 버튼은 null)
            previewSeed: null,
            previewModalId: 'assignment-preview-modal',
            builderModalId: 'assignment-rule-builder-modal',
            // 인플레이스 편집 모달에 넘길 대상 id (null = 신규 추가).
            modalQuestionId: null,
            modalRuleId: null,
            // 프로젝트 전환 시 오래된 응답이 뒤늦게 덮어쓰지 않도록 요청 순번을 센다.
            loadToken: 0,
            // 같은 프로젝트 안에서 영역별(준비 상태·질문·규칙) 다시 받기 순번 — 늦게 온 앞 요청이 새 결과를 덮지 않게
            partSeq: {readiness: 0, schema: 0, rules: 0},
            // 규칙셋 상세 조회 동시성 상한 (프로젝트 전환 시 요청 폭주 방지).
            DETAIL_CONCURRENCY: 6,
            RULE_SET_PAGE_SIZE: 200,
            // 안전 상한: 한 프로젝트에서 가져올 규칙셋 페이지 최대치(무한 루프/폭주 방지).
            RULE_SET_MAX_PAGES: 25,
        };
    },
    computed: {
        ...mapState(['user_id']),
        projectCodeOptions() {
            return getProjectCodeOptions(this.projects);
        },
        keyMap() {
            return buildKeyMap(this.inputKeys);
        },
        missingKeys() {
            return (this.readiness && this.readiness.missing_keys) || [];
        },
        // 평가지 목록은 따로(늦게) 올 수 있어 이름은 여기서 붙인다
        ruleSets() {
            return this.ruleSetsRaw.map((rs) => ({
                ...rs,
                campaignName: getTargetCampaignText(rs.campaign_id, this.campaigns),
            }));
        },
        fallbackCampaignName() {
            const fbId = this.readiness && this.readiness.fallback_campaign_id;
            // 폴백 ID 가 있으면 이름을 못 찾아도 '연결 안 됨'이 아니라 #id 로 표시(#7).
            return fbId != null ? (getTargetCampaignText(fbId, this.campaigns) || `평가지 #${fbId}`) : null;
        },
        selectedProjectText() {
            return getTargetProjectText(this.selectedProjectId, this.projects);
        },
        // 규칙셋 이름 조립용 프로젝트 코드(짧은 형태). 못 찾으면 null.
        selectedProjectCode() {
            const groups = this.projects || [];
            for (const g of groups) {
                const found = (g.projects || []).find((p) => p.id === this.selectedProjectId);
                if (found) return found.project_code;
            }
            return null;
        },
    },
    watch: {
        selectedProjectId(next) {
            // 선택이 바뀌면(전환·해제 포함) 항상 진행 중 조회를 무효화한다.
            // 늦은 이전-프로젝트 응답이 새 상태를 덮어쓰지 않도록 토큰을 올린다(경쟁상태 게이트).
            const token = ++this.loadToken;
            this.clearProjectState();
            if (next) {
                this.loadProjectData(next, token);
            }
            else {
                this.loading = false;
            }
        },
        // 뒤로가기·사이드바 재클릭(빈 query)·링크로 바뀐 URL 을 선택에 맞춘다
        '$route.query.project_id'() {
            if (this.$route.name !== ROUTE_NAME) return;
            this.applyRouteProject();
        },
    },
    // 편집 창이 열린 채 뒤로가기·사이드바로 프로젝트가 바뀌면, 열린 수정 폼이 다른 프로젝트 값으로 저장될 수 있다.
    // 바뀐 입력이 있으면 확인받고, 이동하면 열린 창을 모두 닫는다
    async beforeRouteUpdate(to, from, next) {
        if (to.query.project_id === from.query.project_id) return next();
        next((await this.closeEditorsForNavigation()) ? undefined : false);
    },
    async beforeRouteLeave(to, from, next) {
        next((await this.closeEditorsForNavigation()) ? undefined : false);
    },
    async created() {
        // 평가지 목록은 이름 표시에만 쓰여 기다리지 않는다(도착하면 이름이 다시 계산된다)
        this.loadCampaigns();
        await this.loadProjects();
        this.applyRouteProject();
    },
    methods: {
        async loadProjects() {
            this.projectsLoading = true;
            this.projectsError = '';
            try {
                const res = await this.$axios.get('/project_code');
                this.projects = res.data;
            }
            catch (e) {
                if (e?.response?.status === 401) return;
                this.projectsError = `프로젝트 목록을 불러오지 못했습니다. ${getErrorMessage(e, '')}`.trim();
            }
            finally {
                this.projectsLoading = false;
            }
        },
        async retryProjects() {
            await this.loadProjects();
            this.applyRouteProject();
        },
        async loadCampaigns() {
            try {
                const res = await this.$axios.get('/read_campaigns_industries', {params: {user_id: this.user_id}});
                this.campaigns = res.data || [];
            }
            catch (e) {
                if (e?.response?.status === 401) return;
                this.$EventBus.$emit('toast-failure', '오류', '평가지 목록을 불러오지 못했습니다.');
            }
        },
        async closeEditorsForNavigation() {
            const {questionModal, ruleModal, ruleBuilder} = this.$refs;
            const dirty = [questionModal, ruleModal, ruleBuilder].some((m) => m && m.hasUnsavedChanges);
            if (dirty && !(await this.$showModal({
                type: 'confirm',
                state: 'warning',
                title: '저장하지 않은 입력',
                message: '입력한 내용이 저장되지 않았습니다. 이동하면 입력이 사라집니다. 이동할까요?',
            }))) return false;
            // 닫기 확인을 다시 묻지 않게 미저장 표시를 지우고 닫는다
            if (questionModal) questionModal.closeModal();
            if (ruleModal) ruleModal.closeModal();
            if (ruleBuilder) ruleBuilder.discardAndClose();
            CONSOLE_MODAL_IDS.forEach((id) => this.$bvModal.hide(id));
            return true;
        },
        projectExists(id) {
            return (this.projects || []).some((g) => (g.projects || []).some((p) => p.id === id));
        },
        // URL → 선택. 복원은 방문 기록을 만들지 않고, 잘못된 값 정리는 replace 로 한다
        applyRouteProject() {
            const raw = this.$route.query.project_id;
            if (raw == null) {
                this.selectedProjectId = null;
                return;
            }
            // 목록을 못 받았으면 '없는 프로젝트'로 단정하지 않는다 — URL 은 두고 오류·다시 시도를 보여 준다
            if (this.projects == null) return;
            const id = parseProjectId(raw);
            if (id == null || !this.projectExists(id)) {
                if (raw !== '') {
                    this.$EventBus.$emit('toast-failure', '프로젝트 배정 콘솔', `해당 프로젝트(#${raw})를 찾을 수 없습니다.`);
                }
                this.selectedProjectId = null;
                this.$router.replace({name: ROUTE_NAME, query: {}}).catch(() => {});
                return;
            }
            this.selectedProjectId = id;
        },
        // 사용자가 고르거나 지웠을 때만 URL 을 바꾼다(push — 뒤로가기로 이전 프로젝트로 돌아간다)
        onProjectPicked(id) {
            const next = id != null ? String(id) : undefined;
            if (this.$route.query.project_id === next) return;
            this.$router.push({name: ROUTE_NAME, query: next ? {project_id: next} : {}}).catch(() => {}); // NavigationDuplicated 무시
        },
        clearProjectState() {
            this.readiness = null;
            this.inputKeys = [];
            this.ruleSetsRaw = [];
            this.ruleSetTotal = 0;
            this.ruleSetTruncated = false;
            this.loadError = null;
        },
        reload() {
            if (this.selectedProjectId) {
                const token = ++this.loadToken;
                this.loadError = null;
                this.loadProjectData(this.selectedProjectId, token);
            }
        },
        // 늦은 응답 폐기: 이 조회의 token 이 아직 최신이고, 대상 프로젝트가 여전히 선택 중일 때만 반영.
        isStale(token, projectId) {
            return token !== this.loadToken || projectId !== this.selectedProjectId;
        },
        // 처음 열 때·다시 시도: 전체 스피너. 한 영역이라도 실패하면 어느 단계가 왜 실패했는지 보여 준다
        async loadProjectData(projectId, token) {
            this.loading = true;
            this.loadError = null;
            const results = await Promise.all(['readiness', 'schema', 'rules'].map((part) => this.loadPart(part, projectId, token)));
            if (this.isStale(token, projectId)) return;
            const failed = results.find(Boolean);
            if (failed) {
                this.loadError = `프로젝트 배정 정보(${PART_LABELS[failed.part]})를 불러오지 못했습니다. ${failed.reason}`.trim();
            }
            this.loading = false;
        },
        // 저장·삭제 뒤 바뀐 영역만 다시 받는다. 화면은 그대로 두고 '갱신 중'만 띄우며, 실패하면 토스트(화면 유지)
        async refreshParts(parts) {
            const projectId = this.selectedProjectId;
            if (!projectId) return;
            const token = this.loadToken;
            this.refreshing += 1;
            try {
                const results = await Promise.all(parts.map((part) => this.loadPart(part, projectId, token)));
                const failed = results.find(Boolean);
                if (failed) {
                    this.$EventBus.$emit('toast-failure', '프로젝트 배정 콘솔',
                        `${PART_LABELS[failed.part]} 정보를 다시 불러오지 못했습니다. 화면이 최신이 아닐 수 있습니다. ${failed.reason}`.trim());
                }
            }
            finally {
                this.refreshing -= 1;
            }
        },
        // 영역 하나를 받아 최신 요청일 때만 반영한다. 던지지 않고 실패면 {part, reason} 을 돌려준다
        async loadPart(part, projectId, token) {
            const seq = ++this.partSeq[part];
            const isCurrent = () => !this.isStale(token, projectId) && seq === this.partSeq[part];
            try {
                const data = await this.fetchPart(part, projectId, isCurrent);
                if (isCurrent()) this.applyPart(part, data);
                return null;
            }
            catch (e) {
                if (!isCurrent() || e?.response?.status === 401) return null;
                return {part, reason: getErrorMessage(e, '')};
            }
        },
        async fetchPart(part, projectId, isCurrent) {
            if (part === 'readiness') return (await getCampaignReadiness(projectId)).data;
            if (part === 'schema') return (await getProjectSchema(projectId)).data;
            // 규칙셋 상세(조건 트리)를 채운다. 목록 응답은 root_rule_group 이 얕아
            // 조건 문장을 그리려면 상세가 필요하다. 요청 폭주를 막으려 동시성 상한을 둔다.
            // 상세 실패는 detailError 로 표시(네트워크 오류를 '조건 없음'으로 위장하지 않음).
            const list = await this.fetchAllRuleSets(projectId, isCurrent);
            const items = await this.fetchDetailsBounded(list.items, isCurrent);
            return {...list, items};
        },
        applyPart(part, data) {
            if (part === 'readiness') {
                this.readiness = data;
            }
            else if (part === 'schema') {
                this.inputKeys = (data && data.input_keys) || [];
            }
            else {
                this.ruleSetsRaw = data.items;
                this.ruleSetTotal = data.total;
                this.ruleSetTruncated = data.truncated;
            }
        },
        // 규칙셋 목록을 전 페이지에 걸쳐 모두 가져온다("모든 규칙을 한 화면"). 하드 상한을 두고,
        // 상한 초과 시 truncated 로 알린다. 전환/해제·더 새 요청이 생기면(isCurrent=false) 즉시 중단.
        async fetchAllRuleSets(projectId, isCurrent) {
            const items = [];
            let page = 1;
            let total = null;
            let truncated = false;
            while (page <= this.RULE_SET_MAX_PAGES) {
                const res = await listCampaignRuleSets(
                    {params: {project_id: projectId, page, per_page: this.RULE_SET_PAGE_SIZE}});
                if (!isCurrent()) break;
                const data = res.data || {};
                const pageItems = data.campaign_rule_sets || [];
                if (total == null) total = data.total != null ? data.total : pageItems.length;
                items.push(...pageItems);
                if (pageItems.length < this.RULE_SET_PAGE_SIZE || items.length >= total) break;
                page += 1;
                if (page > this.RULE_SET_MAX_PAGES) truncated = items.length < total;
            }
            return {items, total: total != null ? total : items.length, truncated};
        },
        // 상세 조회를 동시성 상한만큼씩 나눠 순차 실행. 실패 항목은 얕은 목록 + detailError.
        // 요청이 낡으면(프로젝트 전환/해제·더 새 갱신) worker 가 다음 항목을 꺼내기 전에 즉시 멈춘다.
        async fetchDetailsBounded(listed, isCurrent) {
            const out = new Array(listed.length);
            let cursor = 0;
            const worker = async () => {
                while (cursor < listed.length) {
                    if (!isCurrent()) return; // 낡음 → 남은 상세 요청 발사 중단
                    const idx = cursor++;
                    const rs = listed[idx];
                    try {
                        const d = await getCampaignRuleSet(rs.id);
                        out[idx] = d.data;
                    }
                    catch (e) {
                        out[idx] = {...rs, detailError: true};
                    }
                }
            };
            const n = Math.min(this.DETAIL_CONCURRENCY, listed.length || 1);
            await Promise.all(Array.from({length: n}, () => worker()));
            return out;
        },
        // seedRuleSet: 규칙 카드에서 열면 그 규칙(조건 값을 미리 채움), 헤더 버튼이면 null.
        // 모달의 show 이벤트는 동기로 불려 prop 이 반영된 다음 tick 에 연다
        openPreview(seedRuleSet) {
            this.previewSeed = seedRuleSet || null;
            this.$nextTick(() => this.$bvModal.show(this.previewModalId));
        },
        // 신청 질문 추가/편집 — 기존 SchemaInputModal 을 그대로 연다(ManageSchemaInput 과 동일 패턴).
        // '편집' 은 바로 수정 폼으로 연다(모달이 상세·허용값을 다 받은 뒤에 폼을 그린다).
        openQuestionModal(mode, item) {
            this.modalQuestionId = mode === 'add' ? null : (item && item.id);
            this.$refs.questionModal.changeMode(mode);
            this.$bvModal.show('schema-input-detail-modal');
        },
        // 규칙이 쓰는데 신청서에 없는 질문을 그 키 이름이 채워진 폼으로 만든다.
        // modalQuestionId 를 비운 모달 watcher 가 detailData 를 덮은 다음에 시드해야 해서 nextTick 뒤에 연다.
        openMissingQuestion(key) {
            this.modalQuestionId = null;
            this.$nextTick(() => {
                this.$refs.questionModal.openAsNew(key ? {key_name: key} : {});
                this.$bvModal.show('schema-input-detail-modal');
            });
        },
        openOrderModal() {
            this.$bvModal.show('schema-input-order-change-modal');
        },
        // "다른 스키마에서 복사" — 질문 모달(add) 안의 버튼이 요청. picker 목록을 연다.
        openCopyPicker() {
            this.$bvModal.show('schema-input-copy-picker-modal');
        },
        // picker 에서 소스 입력 값을 고르면, 열려있는 질문 모달을 그 값으로 copy-seed 한다.
        // 폼만 채우며(연결 프로젝트는 비움) 저장은 유저가 직접 눌러야 반영된다.
        onCopyImportSelect(sourceId) {
            this.$bvModal.hide('schema-input-copy-picker-modal');
            this.$refs.questionModal.openAsCopy(sourceId);
        },
        // 배정 규칙 추가 — 문장 빌더(등록 질문만 선택 가능).
        openRuleBuilder() {
            this.$bvModal.show(this.builderModalId);
        },
        // 배정 규칙 편집 — 기존 CampaignRuleSetModal 을 그대로 연다(삭제도 모달 내부).
        openRuleModal(mode, item) {
            this.modalRuleId = mode === 'add' ? null : (item && item.id);
            this.$refs.ruleModal.changeMode(mode);
            this.$bvModal.show('campaign-rule-set-detail-modal');
        },
        // 질문 저장·삭제·순서변경 뒤: 준비 상태와 질문만 다시 받는다(규칙셋 상세는 다시 받지 않음)
        onQuestionsChanged() {
            this.refreshParts(['readiness', 'schema']);
        },
        // 규칙 저장·삭제 뒤: 준비 상태와 규칙만 다시 받는다
        onRulesChanged() {
            this.refreshParts(['readiness', 'rules']);
        },
        onBannerCta({action, missingKeys}) {
            if (action === 'create-question') {
                // 빠진 질문이 여럿이면 첫 키부터 — 저장하면 다시 받은 배너가 다음 키를 보여 준다
                this.openMissingQuestion(missingKeys && missingKeys[0]);
            }
            else if (action === 'connect-fallback') {
                // 기본 평가지(project.campaign)는 프로젝트 관리에서 설정한다. 그 프로젝트 수정 창이 바로 열리게 코드를 넘긴다.
                const code = this.selectedProjectCode;
                this.$router.push({name: 'ManagingProjectCode', query: code ? {project_code: code} : {}}).catch(() => {});
            }
        },
        goAdvanced(routeName, tab) {
            const query = DEFAULT_TABS.includes(tab) ? {} : {tab};
            if (PROJECT_FILTER_TABS.includes(tab) && this.selectedProjectId) {
                query.project_id = String(this.selectedProjectId);
            }
            this.$router.push({name: routeName, query}).catch(() => {});
        },
    },
};
</script>

<style lang="scss" scoped>
.gap-2 {
    gap: 0.5rem;
}
</style>
